import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Grid, Button, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { css } from '@emotion/react';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { format, addHours } from 'date-fns';

import {
    SecondaryButton,
    PrimaryButton,
    ResponsiveSidebarDialog,
    SectionTitle,
    ModalContent,
    StickyModalActions,
} from '@/styles/blocks';
import handleGeocode from '@/utilities/handleGeocode';
import geocodeByLatLng from '@/utilities/geocodeByLatLng';
import { buildCanonicalFullAddress, toFullState, toShortState } from '@/utilities/address';
import { asBrowserDate, asDateInTZ } from '@/utilities/convertToISO';
import { mergeFillIfUndefined } from '@/utilities/mergeFillIfUndefined';
import { toE164, toNational, validate as validatePhone } from '@/utilities/formatPhoneNumber';
import { useClientUser } from '@/hooks/useClientUser';
import { useOrgShippers } from '../hooks';
import StartTimeSelect from '@/components/misc/StartTimeSelect';
import { UserContext } from '@/components/App';
import GoogleMap, { Marker } from '@/components/GoogleMap';
import { hasPhoneWarning } from '@/utilities/validatePhoneNumber';

import { Context } from '../store';
import { TextField, Body2 } from '../blocks';
import { MODALS } from '../constants';
import { FREIGHT_TYPES } from '../constants/freightTypes';
import {
    addressSplit,
    sanitizeEnum,
    VALID_LOCATION_TYPES,
    VALID_DROPOFF_TYPES as VALID_PICKUP_TYPES,
} from '../utilities/processOrders';
import {
    buildOrderTypeOptions,
    resolveSelectedOptionValue,
    resolveDefaultSeedPatch,
} from '../utilities/orderTypeOptions';

import {
    Name,
    Phone,
    Email,
    SecondaryContactBtn,
    StoreSelect,
    SingleDatePicker,
    Comments,
    Address,
    AddressLine2,
    City,
    State,
    Zip,
    LocationType,
    PickupType,
    PICKUP_INFO_FIELDS,
} from './InputFields';
import { Info } from '@material-ui/icons';
import { buildPickupInfoAutocompletePatch } from './pickupInfoAutocompletePatch';
import { carrierDisplayName } from '@/utilities/carrierAlias';

// Avoid browser-GPS fallback
const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };

// Stable empty-array reference for the memo fallbacks below. Returning a fresh
// `[]` from `locations` on every state.pickupInfo change re-fired the defaults
// effect (which lists `locations` as a dependency), and that effect writes a new
// state.pickupInfo reference via mergeFillIfUndefined — an infinite render loop.
const EMPTY_ARRAY = [];

const ContactFields = ({ pickupObj, isSecondary, hasError, updatePickup, setDirty, isDirty }) => {
    const name = isSecondary ? PICKUP_INFO_FIELDS.SECONDARY_NAME : PICKUP_INFO_FIELDS.NAME;
    const phone = isSecondary ? PICKUP_INFO_FIELDS.SECONDARY_PHONE : PICKUP_INFO_FIELDS.PHONE;
    const email = isSecondary ? PICKUP_INFO_FIELDS.SECONDARY_EMAIL : PICKUP_INFO_FIELDS.EMAIL;

    return (
        <Grid
            container
            justifyContent="space-between"
            css={css`
                margin-bottom: 20px;
            `}
        >
            <Grid item xs={12} sm={6} md={4}>
                <Name
                    value={pickupObj[name]}
                    onChange={(e) => updatePickup({ [name]: e.target.value })}
                    onBlur={() => {
                        setDirty(name);
                    }}
                    error={hasError[name] && isDirty[name]}
                    isSecondary={isSecondary}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Phone
                    value={toNational(pickupObj[phone])}
                    onChange={(e) => updatePickup({ [phone]: toE164(e.target.value) })}
                    onBlur={() => {
                        setDirty(phone);
                    }}
                    error={hasError[phone] && isDirty[phone]}
                    warning={hasPhoneWarning(pickupObj[phone])}
                    isSecondary={isSecondary}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <Email
                    value={pickupObj[email]}
                    onChange={(e) => updatePickup({ [email]: e.target.value })}
                    onBlur={() => {
                        setDirty(email);
                    }}
                    isSecondary={isSecondary}
                />
            </Grid>
        </Grid>
    );
};

const PickupInfoModal = ({ callbacks: { onClose } }) => {
    const { state, callbacks } = useContext(Context);
    const open = state.current === MODALS.PICKUP_INFO;
    const {
        user_id,
        circles,
        shipping_partners,
        settings,
        pricing_overrides,
        isCarrierAdmin,
        locations: clientLocations,
        partnerCarriersById,
        ...client
    } = useClientUser();
    const { admin_clients } = useContext(UserContext);

    // Carrier admins pick from shippers in their org; everyone else from their
    // formal carrier partnerships. Same entry shape, so the dropdown is identical.
    const orgShippers = useOrgShippers();
    const shippers = isCarrierAdmin && orgShippers.length > 0 ? orgShippers : shipping_partners;
    // Carrier admins place orders on a shipper's behalf, so they must always pick one.
    const requireShipper = isCarrierAdmin || !!settings?.import?.requireShippingPartner;

    const [CLIENT_DEFAULTS, setDefaults] = useState({
        isCustom: false,
        is_same_day: false,
    });
    const [hasSecondaryContact, setHasSecondaryContact] = useState(null);
    const [hasNewAddressOverride, setHasNewAddress] = useState(null);
    const [storeOverride, setStore] = useState(null);
    const [isDirty, setIsDirty] = useState({});
    const [viewMapOverride, setViewMap] = useState(false);
    // True while the defaults effect's geocode is in flight. Gates the Next
    // button so a fast click can't fire advance() with a state.pickupInfo that
    // hasn't received the resolved address parts yet (lat/lng/street/city/...).
    const [geocodeInflight, setGeocodeInflight] = useState(false);

    const carriers = useMemo(() => {
        if (circles?.['auto-assign-by-zip']) return [];
        return (admin_clients || []).filter((client) => client?.user?.roles?.['CARRIER']);
    }, [admin_clients, circles]);

    const carrierPartnerships = useMemo(() => {
        const carrierId = state.pickupInfo.warehouse_carrier_id || state.pickupInfo.carrier_id;
        if (carrierId) {
            return carriers.find((carrier) => carrier.client_id === carrierId)?.shipper_partnerships || EMPTY_ARRAY;
        }
        return EMPTY_ARRAY;
    }, [state.pickupInfo, carriers]);

    const locations = useMemo(() => {
        const carrierId = state.pickupInfo.warehouse_carrier_id || state.pickupInfo.carrier_id;
        if (carrierId) {
            return carriers.find((carrier) => carrier.client_id === carrierId)?.locations || EMPTY_ARRAY;
        }
        return clientLocations || EMPTY_ARRAY;
    }, [carriers, clientLocations, state.pickupInfo]);

    const setDirty = (field) => setIsDirty((prev) => ({ ...prev, [field]: true }));

    const reset = () => {
        setHasSecondaryContact(null);
        setHasNewAddress(null);
        setStore(null);
        setViewMap(false);
    };

    const updatePickup = (updates) => {
        // A carrier picked here is a human choice, so it travels with its provenance -- ingest
        // trusts what it is sent rather than inferring DIRECT from the carrier_id alone.
        callbacks.updatePickupInfo(
            'carrier_id' in updates
                ? { ...updates, carrier_assignment_source: updates.carrier_id ? 'DIRECT' : null }
                : updates
        );
    };

    useEffect(() => {
        // Mirror computed defaults into BOTH local CLIENT_DEFAULTS (for the display
        // merge in `combined`) AND state.pickupInfo (so they're already persisted
        // when the user clicks Next). advance() reads state.pickupInfo via a
        // closure captured at the previous render; writing only CLIENT_DEFAULTS
        // here would leave state.pickupInfo missing values like pickup_name /
        // pickup_phone even though the form displayed them — see the followup
        // doc for context.
        const writeDefaults = (payload) => {
            setDefaults((prev) => ({ ...prev, ...payload }));
            // Use a functional payload with "fill-if-undefined" semantics so a
            // user-cleared field (empty string) is preserved as cleared, and so
            // re-runs of this effect (the `locations` dep recomputes when
            // state.pickupInfo changes) never clobber user input.
            callbacks.updatePickupInfo((prev) => mergeFillIfUndefined(prev, payload));
        };

        const calcDefaults = async (address, additional) => {
            setGeocodeInflight(true);
            try {
                const geoResults = await handleGeocode({ address });

                return writeDefaults({
                    [PICKUP_INFO_FIELDS.LAT]: geoResults.lat,
                    [PICKUP_INFO_FIELDS.LONG]: geoResults.lng,
                    pickupaddress: geoResults.address,
                    pickupfulladdress: geoResults.address,
                    [PICKUP_INFO_FIELDS.STREET]: geoResults.street,
                    [PICKUP_INFO_FIELDS.STATE]: geoResults.state,
                    [PICKUP_INFO_FIELDS.CITY]: geoResults.city,
                    [PICKUP_INFO_FIELDS.ZIP]: geoResults.zip,
                    ...additional,
                });
            } finally {
                setGeocodeInflight(false);
            }
        };

        const preferredLocationIdx = locations.findIndex((location) => location.preferred_inbound) || 0;
        const preferredLocation = locations[preferredLocationIdx];
        if (preferredLocation) {
            setStore(preferredLocationIdx);
            const defaultAddress = preferredLocation.address
                ? preferredLocation.address
                : `${preferredLocation.business_address}, ${preferredLocation.business_city}, ${preferredLocation.business_state} ${preferredLocation.business_zip}`;

            calcDefaults(defaultAddress, {
                [PICKUP_INFO_FIELDS.LOCATION_TYPE]:
                    sanitizeEnum(preferredLocation.location_type, VALID_PICKUP_TYPES) || 'Business',
                [PICKUP_INFO_FIELDS.PICKUP_TYPE]:
                    sanitizeEnum(preferredLocation.location_info, VALID_LOCATION_TYPES) || 'dock',
                [PICKUP_INFO_FIELDS.ADDRESS_LINE_2]: preferredLocation?.business_unit,
                [PICKUP_INFO_FIELDS.NAME]: preferredLocation.contact_name || client?.businessName || '',
                [PICKUP_INFO_FIELDS.PHONE]: validatePhone(preferredLocation.contact_phone)
                    ? toE164(preferredLocation.contact_phone)
                    : client?.businessPhone
                    ? toE164(client.businessPhone)
                    : '',
                [PICKUP_INFO_FIELDS.EMAIL]: preferredLocation.contact_email || client?.email || '',
            });
        } else {
            writeDefaults({
                [PICKUP_INFO_FIELDS.NAME]: client?.businessName || '',
                [PICKUP_INFO_FIELDS.PHONE]: client?.businessPhone ? toE164(client.businessPhone) : '',
                [PICKUP_INFO_FIELDS.EMAIL]: client?.email || '',
            });
        }
    }, [client?.businessName, client?.businessPhone, client?.email, locations]);

    const [hasNewAddress, store, combined] = useMemo(() => {
        const hasDefault = CLIENT_DEFAULTS?.pickupaddress?.length > 0;

        let store = storeOverride;
        if (state.pickupInfo.pickupaddress && locations.length > 0) {
            const match = locations.findIndex(
                (location) =>
                    location.business_address === state.pickupInfo.pickupstreetaddress &&
                    location.business_zip === state.pickupInfo.pickupzip
            );
            store = match > -1 ? match : null;
        }

        let hasNewAddress = false;
        if (hasNewAddressOverride !== null) {
            hasNewAddress = hasNewAddressOverride;
        } else {
            hasNewAddress =
                locations.length === 0 ||
                ((hasDefault || state.pickupInfo.pickupaddress) && store === null) ||
                state.pickupInfo.isCustom;
        }

        return [
            hasNewAddress,
            store,
            {
                ...CLIENT_DEFAULTS,
                ...state.pickupInfo,
            },
        ];
    }, [state.pickupInfo, CLIENT_DEFAULTS, hasNewAddressOverride, locations]);

    useEffect(() => {
        const calc = (fields) => fields.filter((val) => val && val.length > 0).join(', ');

        if (combined && combined.isCustom) {
            const address = calc([
                combined[PICKUP_INFO_FIELDS.STREET],
                combined[PICKUP_INFO_FIELDS.CITY],
                combined[PICKUP_INFO_FIELDS.STATE],
                combined[PICKUP_INFO_FIELDS.ZIP],
            ]);
            const fullAddress = calc([
                combined[PICKUP_INFO_FIELDS.STREET],
                combined[PICKUP_INFO_FIELDS.ADDRESS_LINE_2],
                combined[PICKUP_INFO_FIELDS.CITY],
                combined[PICKUP_INFO_FIELDS.STATE],
                combined[PICKUP_INFO_FIELDS.ZIP],
            ]);

            updatePickup({
                pickupaddress: address,
                pickupfulladdress: fullAddress,
            });
        }
    }, [
        combined.isCustom,
        combined[PICKUP_INFO_FIELDS.STREET],
        combined[PICKUP_INFO_FIELDS.CITY],
        combined[PICKUP_INFO_FIELDS.STATE],
        combined[PICKUP_INFO_FIELDS.ZIP],
        combined[PICKUP_INFO_FIELDS.ADDRESS_LINE_2],
    ]);

    const requiredFields = useMemo(() => {
        if (state.groupedPickup) {
            return [];
        }

        const required = [];

        if (hasSecondaryContact) {
            required.push(PICKUP_INFO_FIELDS.SECONDARY_NAME, PICKUP_INFO_FIELDS.SECONDARY_PHONE);
        }

        if (!state?.pickupInfo?.is_middle_mile || !!state?.pickupInfo?.is_middle_mile_origin) {
            required.push(PICKUP_INFO_FIELDS.NAME, PICKUP_INFO_FIELDS.PHONE);
            if (hasNewAddress) {
                required.push(
                    PICKUP_INFO_FIELDS.STREET,
                    PICKUP_INFO_FIELDS.CITY,
                    PICKUP_INFO_FIELDS.STATE,
                    PICKUP_INFO_FIELDS.ZIP,
                    PICKUP_INFO_FIELDS.LOCATION_TYPE,
                    PICKUP_INFO_FIELDS.PICKUP_TYPE
                );
            } else {
                required.push(PICKUP_INFO_FIELDS.STORE);
            }

            // The Shipper field only renders in this branch, so only require it here.
            if (requireShipper) {
                required.push(PICKUP_INFO_FIELDS.SHIPPER_ID);
            }
        }

        return required;
    }, [hasSecondaryContact, hasNewAddress, state?.pickupInfo, state?.groupedPickup, requireShipper]);

    const viewMap = viewMapOverride;

    const orderTz = useMemo(() => {
        return combined[PICKUP_INFO_FIELDS.ZIP]
            ? zipcode_to_timezone.lookup(combined[PICKUP_INFO_FIELDS.ZIP])
            : Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, [combined]);

    const hasError = useMemo(() => {
        return Object.fromEntries(
            requiredFields
                .filter((field) => {
                    const isEmpty = !combined[field] || combined[field].length === 0;
                    switch (field) {
                        case PICKUP_INFO_FIELDS.STORE:
                            return !hasNewAddress && store === null;
                        default:
                            return isEmpty;
                    }
                })
                .map((field) => [field, true])
        );
    }, [combined, requiredFields, hasNewAddress, store]);

    const isValid = useMemo(() => {
        return !Object.values(hasError).some((isInvalid) => isInvalid);
    }, [hasError]);

    const latLng = useMemo(() => {
        if (combined) {
            return combined[PICKUP_INFO_FIELDS.LAT] && combined[PICKUP_INFO_FIELDS.LONG]
                ? { lat: combined[PICKUP_INFO_FIELDS.LAT], lng: combined[PICKUP_INFO_FIELDS.LONG] }
                : null;
        }

        return null;
    }, [combined]);

    // Geocode before map
    const ensurePickupCoords = async () => {
        const address =
            combined.pickupfulladdress ||
            combined[PICKUP_INFO_FIELDS.FULL_ADDRESS] ||
            [
                combined[PICKUP_INFO_FIELDS.STREET],
                combined[PICKUP_INFO_FIELDS.CITY],
                combined[PICKUP_INFO_FIELDS.STATE],
                combined[PICKUP_INFO_FIELDS.ZIP],
            ]
                .filter(Boolean)
                .join(', ');

        if (!address) return;

        setGeocodeInflight(true);
        try {
            const geo = await handleGeocode({ address });
            updatePickup({ [PICKUP_INFO_FIELDS.LAT]: geo.lat, [PICKUP_INFO_FIELDS.LONG]: geo.lng });
        } catch (err) {
            console.warn('Pickup map geocode failed:', err);
        } finally {
            setGeocodeInflight(false);
        }
    };

    const handleToggleMap = async () => {
        if (!viewMap && !latLng) {
            await ensurePickupCoords();
        }
        setViewMap((prev) => !prev);
    };

    const handleStoreSelect = async (e) => {
        setStore(e.target.value);

        const _store = locations[e.target.value];
        const [address1, address2] = addressSplit(_store.business_address);

        const updates = {
            [PICKUP_INFO_FIELDS.IS_CUSTOM]: false,
            pickupfulladdress: _store.address,
            [PICKUP_INFO_FIELDS.STREET]: address1,
            [PICKUP_INFO_FIELDS.CITY]: _store.business_city,
            [PICKUP_INFO_FIELDS.STATE]: _store.business_state,
            [PICKUP_INFO_FIELDS.ZIP]: _store.business_zip,
            [PICKUP_INFO_FIELDS.COMMENTS]: _store.shipper_pu_comments || '',
            [PICKUP_INFO_FIELDS.FULL_ADDRESS]: _store.address,
            [PICKUP_INFO_FIELDS.LOCATION_TYPE]: sanitizeEnum(_store.location_type, VALID_PICKUP_TYPES) || 'Business',
            [PICKUP_INFO_FIELDS.PICKUP_TYPE]: sanitizeEnum(_store.location_info, VALID_LOCATION_TYPES) || 'dock',
            [PICKUP_INFO_FIELDS.ADDRESS_LINE_2]: address2,
            [PICKUP_INFO_FIELDS.NAME]: _store?.contact_name || client?.businessName || '',
            [PICKUP_INFO_FIELDS.PHONE]: _store?.contact_phone
                ? toE164(_store.contact_phone)
                : client?.businessPhone
                ? toE164(client.businessPhone)
                : '',
            [PICKUP_INFO_FIELDS.EMAIL]: _store?.contact_email || client?.email || '',
        };

        // Stored coords win
        if (_store.lat && _store.lng) {
            updatePickup({ ...updates, [PICKUP_INFO_FIELDS.LAT]: _store.lat, [PICKUP_INFO_FIELDS.LONG]: _store.lng });
            return;
        }

        updatePickup(updates);

        const storeAddress =
            _store.address ||
            `${_store.business_address}, ${_store.business_city}, ${_store.business_state} ${_store.business_zip}`;
        setGeocodeInflight(true);
        try {
            const geo = await handleGeocode({ address: storeAddress });
            updatePickup({ [PICKUP_INFO_FIELDS.LAT]: geo.lat, [PICKUP_INFO_FIELDS.LONG]: geo.lng });
        } catch (err) {
            console.warn('Store address geocode failed:', err);
        } finally {
            setGeocodeInflight(false);
        }
    };

    const options = useMemo(() => buildOrderTypeOptions({ pricingOverrides: pricing_overrides }), [pricing_overrides]);
    const selected = resolveSelectedOptionValue(state?.pickupInfo, options);
    const selectedOrderType = options.find((option) => option.value === selected);

    // Seed the default only in the receiving-on / mm-fm-off gap store.js leaves.
    // Mount-only: assumes store.js's circles effect has already set pickupInfo when
    // import-default-mm-fm is on; the resolveDefaultSeedPatch guard no-ops if so.
    useEffect(() => {
        const patch = resolveDefaultSeedPatch({ pickupInfo: state.pickupInfo, circles, options });
        if (patch) callbacks.updatePickupInfo((prev) => mergeFillIfUndefined(prev, patch));
    }, []);

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={() => {
                reset();
                onClose();
            }}
        >
            <ModalContent width={620}>
                <Grid
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <SectionTitle>Freight Type</SectionTitle>
                </Grid>

                <Grid
                    container
                    direction="column"
                    css={css`
                        margin-bottom: 36px;
                    `}
                >
                    {/*
                       Freight Type intentionally has no error binding: state.freightType is
                       initialized to 'household' in store.js and the select has no empty option,
                       so it cannot become null via the UI. If that default is ever removed, add
                       FREIGHT_TYPE to requiredFields and wire an `error` prop here.
                    */}
                    <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Freight Type"
                        value={state.freightType}
                        onChange={(e) => callbacks.setFreightType(e.target.value)}
                    >
                        {FREIGHT_TYPES.map(
                            (freight) =>
                                !freight.hidden && (
                                    <MenuItem key={`freight-select-${freight.value}`} value={freight.value}>
                                        {freight.label}
                                        {freight.info && (
                                            <Typography
                                                variant="caption"
                                                css={css`
                                                    margin-left: 8px;
                                                    color: #666;
                                                `}
                                            >
                                                {freight.info}
                                            </Typography>
                                        )}
                                    </MenuItem>
                                )
                        )}
                    </TextField>
                </Grid>

                {!state.groupedPickup ? (
                    <>
                        <Grid
                            css={css`
                                margin-bottom: 24px;
                            `}
                        >
                            <SectionTitle>Pickup / Warehouse Information</SectionTitle>
                        </Grid>

                        <Grid
                            container
                            direction="column"
                            css={css`
                                margin-bottom: 20px;
                                margin-top: 20px;
                            `}
                        >
                            <TextField
                                select
                                variant="outlined"
                                label="Order Type"
                                value={selected}
                                onChange={(e) => {
                                    const option = options.find((o) => o.value === e.target.value);
                                    if (option) updatePickup(option.patch);
                                }}
                                fullWidth
                            >
                                {options.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {selectedOrderType?.description ? (
                                <Body2
                                    css={css`
                                        margin-top: 8px;
                                    `}
                                >
                                    {selectedOrderType.description}
                                </Body2>
                            ) : null}
                        </Grid>

                        {(!state?.pickupInfo?.is_middle_mile || state?.pickupInfo?.is_middle_mile_origin) && (
                            <>
                                <ContactFields
                                    pickupObj={combined}
                                    hasError={hasError}
                                    updatePickup={updatePickup}
                                    isDirty={isDirty}
                                    setDirty={setDirty}
                                />
                                {hasSecondaryContact ? (
                                    <ContactFields
                                        pickupObj={combined}
                                        hasError={hasError}
                                        isSecondary={true}
                                        updatePickup={updatePickup}
                                        isDirty={isDirty}
                                        setDirty={setDirty}
                                    />
                                ) : null}

                                <Grid
                                    css={css`
                                        margin-bottom: 24px;
                                    `}
                                >
                                    <SecondaryContactBtn
                                        hasSecondaryContact={hasSecondaryContact}
                                        onClick={() => {
                                            setHasSecondaryContact(!hasSecondaryContact);
                                            updatePickup({
                                                [PICKUP_INFO_FIELDS.SECONDARY_NAME]: null,
                                                [PICKUP_INFO_FIELDS.SECONDARY_PHONE]: null,
                                                [PICKUP_INFO_FIELDS.SECONDARY_EMAIL]: null,
                                            });
                                        }}
                                        state={combined}
                                        error={[
                                            PICKUP_INFO_FIELDS.STREET,
                                            PICKUP_INFO_FIELDS.STATE,
                                            PICKUP_INFO_FIELDS.CITY,
                                            PICKUP_INFO_FIELDS.ZIP,
                                        ].some((i) => hasError[i] && isDirty[i])}
                                    />
                                </Grid>

                                {carriers.length > 0 ? (
                                    <Grid
                                        container
                                        direction="row"
                                        css={css`
                                            margin-bottom: 20px;
                                            align-items: center;
                                            gap: 16px;
                                        `}
                                    >
                                        <Grid item xs={12}>
                                            <TextField
                                                select
                                                variant="outlined"
                                                fullWidth
                                                label="Carrier"
                                                value={
                                                    state.pickupInfo.warehouse_carrier_id || state.pickupInfo.carrier_id
                                                }
                                                onChange={(e) =>
                                                    updatePickup({
                                                        carrier_id: e.target.value,
                                                        warehouse_carrier_id: null,
                                                        // A new carrier's subcontractor partnerships aren't the
                                                        // previous carrier's, so a stale contractor_id can't carry over.
                                                        contractor_id: null,
                                                    })
                                                }
                                            >
                                                {carriers.map((carrier) => (
                                                    <MenuItem key={carrier.client_id} value={carrier.client_id}>
                                                        {carrierDisplayName(carrier, partnerCarriersById)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                ) : null}

                                {carrierPartnerships.length > 0 ? (
                                    <Grid
                                        container
                                        direction="row"
                                        css={css`
                                            margin-bottom: 20px;
                                            align-items: center;
                                            gap: 16px;
                                        `}
                                    >
                                        <Grid item xs={12}>
                                            <TextField
                                                select
                                                variant="outlined"
                                                fullWidth
                                                label="Contractor"
                                                value={state.pickupInfo.contractor_id || 'NONE'}
                                                onChange={(e) => {
                                                    const next = e.target.value;
                                                    // carrier_id/warehouse_carrier_id stay put — the selected
                                                    // Carrier remains the carrier of record either way. Only
                                                    // contractor_id (the subcontracted carrier that actually
                                                    // executes final mile) changes here.
                                                    updatePickup({
                                                        contractor_id: next === 'NONE' ? null : next,
                                                    });
                                                }}
                                            >
                                                <MenuItem value="NONE">None</MenuItem>
                                                {carrierPartnerships.map((partnership) => (
                                                    <MenuItem
                                                        key={partnership.partnership_id}
                                                        value={partnership.carrier_id}
                                                    >
                                                        {carrierDisplayName(partnership.carrier, partnerCarriersById) ||
                                                            'Unknown'}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                ) : null}

                                <Grid
                                    css={css`
                                        margin-bottom: 20px;
                                        display: flex;
                                        align-items: center;
                                        gap: 16px;
                                    `}
                                    container
                                    direction="row"
                                >
                                    <Grid item xs={8}>
                                        <StoreSelect
                                            value={store}
                                            onChange={handleStoreSelect}
                                            onBlur={() => {
                                                setDirty(PICKUP_INFO_FIELDS.STORE);
                                            }}
                                            error={
                                                hasError[PICKUP_INFO_FIELDS.STORE] && isDirty[PICKUP_INFO_FIELDS.STORE]
                                            }
                                            required={!hasNewAddress}
                                            storeList={locations}
                                        />
                                    </Grid>
                                    {locations.length > 0 && (
                                        <Grid item xs={3}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => setHasNewAddress(!hasNewAddress)}
                                                css={css`
                                                    text-transform: none;
                                                    ${hasNewAddress &&
                                                    `background-color: #e3f2fd; border-color: #2196f3; color: #2196f3;`}
                                                `}
                                            >
                                                {hasNewAddress ? <>New Address ✓</> : 'New Address'}
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                                {hasNewAddress && (
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            margin-bottom: 24px;
                                        `}
                                    >
                                        <Grid
                                            container
                                            spacing={2}
                                            css={css`
                                                margin-bottom: 20px;
                                            `}
                                        >
                                            <Grid item xs={9}>
                                                <Address
                                                    handleAddressUpdate={(value, split) => {
                                                        updatePickup(buildPickupInfoAutocompletePatch(value, split));
                                                    }}
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.STREET);
                                                        setDirty(PICKUP_INFO_FIELDS.STATE);
                                                        setDirty(PICKUP_INFO_FIELDS.CITY);
                                                        setDirty(PICKUP_INFO_FIELDS.ZIP);
                                                    }}
                                                    state={combined}
                                                    error={[
                                                        PICKUP_INFO_FIELDS.STREET,
                                                        PICKUP_INFO_FIELDS.STATE,
                                                        PICKUP_INFO_FIELDS.CITY,
                                                        PICKUP_INFO_FIELDS.ZIP,
                                                    ].some((i) => hasError[i] && isDirty[i])}
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <AddressLine2
                                                    value={combined[PICKUP_INFO_FIELDS.ADDRESS_LINE_2]}
                                                    onChange={(e) =>
                                                        updatePickup({
                                                            [PICKUP_INFO_FIELDS.ADDRESS_LINE_2]: e.target.value,
                                                        })
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            spacing={2}
                                            css={css`
                                                margin-bottom: 24px;
                                            `}
                                        >
                                            <Grid item xs={12} sm={6} md={4}>
                                                <City
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.CITY] &&
                                                        isDirty[PICKUP_INFO_FIELDS.CITY]
                                                    }
                                                    value={combined[PICKUP_INFO_FIELDS.CITY]}
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.CITY);
                                                    }}
                                                    onChange={(e) =>
                                                        updatePickup({ [PICKUP_INFO_FIELDS.CITY]: e.target.value })
                                                    }
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <State
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.STATE] &&
                                                        isDirty[PICKUP_INFO_FIELDS.STATE]
                                                    }
                                                    value={toFullState(combined[PICKUP_INFO_FIELDS.STATE])}
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.STATE);
                                                    }}
                                                    onChange={(e) =>
                                                        updatePickup({
                                                            [PICKUP_INFO_FIELDS.STATE]: toShortState(e.target.value),
                                                        })
                                                    }
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Zip
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.ZIP] &&
                                                        isDirty[PICKUP_INFO_FIELDS.ZIP]
                                                    }
                                                    value={combined[PICKUP_INFO_FIELDS.ZIP]}
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.ZIP);
                                                    }}
                                                    onChange={(e) =>
                                                        updatePickup({ [PICKUP_INFO_FIELDS.ZIP]: e.target.value })
                                                    }
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            direction="column"
                                            css={css`
                                                margin-bottom: 24px;
                                            `}
                                        >
                                            <Grid item container>
                                                <Button
                                                    css={css`
                                                        color: #fff;
                                                    `}
                                                    onClick={handleToggleMap}
                                                    disabled={geocodeInflight}
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    {viewMap ? 'Hide map' : 'Pin location on map'}
                                                </Button>
                                            </Grid>

                                            {viewMap && (
                                                <Grid
                                                    item
                                                    css={css`
                                                        height: 350px;
                                                        margin-top: 24px;
                                                        border-radius: 6px;
                                                        border: ${hasError[PICKUP_INFO_FIELDS.LAT] ||
                                                        hasError[PICKUP_INFO_FIELDS.LONG]
                                                            ? `1px solid #f44336;`
                                                            : '1px solid transparent;'};
                                                    `}
                                                >
                                                    <GoogleMap
                                                        zoom={latLng ? 15 : 4}
                                                        onClick={async (e) => {
                                                            const lat = e.latLng.lat();
                                                            const lng = e.latLng.lng();

                                                            updatePickup({
                                                                [PICKUP_INFO_FIELDS.LAT]: lat,
                                                                [PICKUP_INFO_FIELDS.LONG]: lng,
                                                                [PICKUP_INFO_FIELDS.IS_CUSTOM]: true,
                                                            });

                                                            // Sync dropped-pin text
                                                            const reverse = await geocodeByLatLng({ lat, lng });
                                                            if (reverse) {
                                                                const reconciled = {};
                                                                if (reverse.address) {
                                                                    reconciled.pickupaddress = reverse.address;
                                                                    // Reverse geocode drops unit
                                                                    reconciled.pickupfulladdress =
                                                                        buildCanonicalFullAddress({
                                                                            street: reverse.street,
                                                                            unit: combined[
                                                                                PICKUP_INFO_FIELDS.ADDRESS_LINE_2
                                                                            ],
                                                                            city: reverse.city,
                                                                            state: reverse.state,
                                                                            zip: reverse.zip,
                                                                        });
                                                                }
                                                                if (reverse.street)
                                                                    reconciled[PICKUP_INFO_FIELDS.STREET] =
                                                                        reverse.street;
                                                                if (reverse.city)
                                                                    reconciled[PICKUP_INFO_FIELDS.CITY] = reverse.city;
                                                                if (reverse.state)
                                                                    reconciled[PICKUP_INFO_FIELDS.STATE] =
                                                                        reverse.state;
                                                                if (reverse.zip)
                                                                    reconciled[PICKUP_INFO_FIELDS.ZIP] = reverse.zip;
                                                                updatePickup(reconciled);
                                                            }
                                                        }}
                                                        draggableCursor="pointer"
                                                        gestureHandling="greedy"
                                                        center={latLng || DEFAULT_MAP_CENTER}
                                                    >
                                                        {latLng && <Marker position={latLng} />}
                                                    </GoogleMap>
                                                </Grid>
                                            )}
                                        </Grid>

                                        <Grid item container xs={12} spacing={2}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <LocationType
                                                    value={combined[PICKUP_INFO_FIELDS.LOCATION_TYPE]}
                                                    onChange={(e) =>
                                                        updatePickup({
                                                            [PICKUP_INFO_FIELDS.LOCATION_TYPE]: e.target.value,
                                                        })
                                                    }
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.LOCATION_TYPE);
                                                    }}
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.LOCATION_TYPE] &&
                                                        isDirty[PICKUP_INFO_FIELDS.LOCATION_TYPE]
                                                    }
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <PickupType
                                                    disabled={
                                                        !combined[PICKUP_INFO_FIELDS.LOCATION_TYPE] ||
                                                        combined[PICKUP_INFO_FIELDS.LOCATION_TYPE].length === 0
                                                    }
                                                    value={combined[PICKUP_INFO_FIELDS.PICKUP_TYPE]}
                                                    locationType={combined[PICKUP_INFO_FIELDS.LOCATION_TYPE]}
                                                    onChange={(e) =>
                                                        updatePickup({
                                                            [PICKUP_INFO_FIELDS.PICKUP_TYPE]: e.target.value,
                                                        })
                                                    }
                                                    onBlur={() => {
                                                        setDirty(PICKUP_INFO_FIELDS.PICKUP_TYPE);
                                                    }}
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.PICKUP_TYPE] &&
                                                        isDirty[PICKUP_INFO_FIELDS.PICKUP_TYPE]
                                                    }
                                                    required={hasNewAddress}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid
                                    item
                                    xs={12}
                                    css={css`
                                        margin-bottom: 24px;
                                    `}
                                >
                                    <Comments
                                        value={combined[PICKUP_INFO_FIELDS.COMMENTS]}
                                        onChange={(e) =>
                                            updatePickup({ [PICKUP_INFO_FIELDS.COMMENTS]: e.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid
                                    container
                                    alignItems="center"
                                    css={css`
                                        margin-bottom: 24px;
                                        display: flex;
                                        align-items: center;
                                    `}
                                >
                                    <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                        <SectionTitle style={{ margin: 0 }}>Availability</SectionTitle>
                                        <Tooltip
                                            title="This is the earliest date that these orders can be scheduled for delivery. Will be defaulted to today if left blank."
                                            placement="top"
                                        >
                                            <Info style={{ marginLeft: 8, color: '#59B863', display: 'flex' }} />
                                        </Tooltip>
                                    </Grid>
                                </Grid>

                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        margin-bottom: 24px;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0.8;
                                            flex-basis: 0;
                                            margin-right: 16px;
                                        `}
                                    >
                                        <SingleDatePicker
                                            label="Available Date"
                                            value={
                                                combined.firstAvailableDate
                                                    ? format(asBrowserDate(combined.firstAvailableDate), 'yyyy-MM-dd')
                                                    : null
                                            }
                                            onChange={(e) => {
                                                let dateStr = null;
                                                let hour = null;

                                                if (e.target.value) {
                                                    const utc = new Date(
                                                        new Date(e.target.value).setUTCHours(0, 0, 0, 0)
                                                    ).toISOString();
                                                    dateStr = asDateInTZ(utc, orderTz).toISOString();

                                                    hour = addHours(
                                                        new Date(new Date(dateStr).setUTCMinutes(0, 0, 0)),
                                                        8
                                                    ).toISOString();
                                                }

                                                updatePickup({
                                                    firstAvailableDate: dateStr,
                                                    delivery_date: dateStr,
                                                    pickup_window_end: hour ? hour : null,
                                                });
                                            }}
                                            disableDate={combined.is_same_day}
                                        />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0.6;
                                            flex-basis: 0;
                                            margin-right: 16px;
                                            min-width: 120px;
                                        `}
                                    >
                                        <TextField
                                            select
                                            type="text"
                                            variant="outlined"
                                            fullWidth
                                            label="Pickup"
                                            value={combined.is_same_day}
                                            onChange={(e) => {
                                                const utc = new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString();
                                                const hour = new Date(
                                                    addHours(new Date().setMinutes(0, 0, 0), 1)
                                                ).toISOString();
                                                const today = asDateInTZ(utc, orderTz).toISOString();

                                                updatePickup({
                                                    is_same_day: e.target.value,
                                                    ...(e.target.value
                                                        ? {
                                                              delivery_date: today,
                                                              pickup_date: today,
                                                              firstAvailableDate: today,
                                                              pickup_window_end: hour,
                                                          }
                                                        : {
                                                              delivery_date: null,
                                                              pickup_date: null,
                                                              firstAvailableDate: null,
                                                              pickup_window_end: null,
                                                          }),
                                                });
                                            }}
                                        >
                                            {pricing_overrides.some((override) => override.override_type === 'GIG') && (
                                                <MenuItem key="yes" value={true}>
                                                    Now
                                                </MenuItem>
                                            )}
                                            <MenuItem key="no" value={false}>
                                                Later
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                        `}
                                    >
                                        <StartTimeSelect
                                            onChange={(e) => {
                                                updatePickup({
                                                    pickup_window_end: new Date(e.target.value).toISOString(),
                                                });
                                            }}
                                            value={
                                                combined.pickup_window_end
                                                    ? new Date(combined.pickup_window_end).getTime()
                                                    : 0
                                            }
                                            timeZone={orderTz}
                                            deliveryDate={
                                                combined.firstAvailableDate
                                                    ? combined.firstAvailableDate
                                                    : new Date().toISOString()
                                            }
                                            disabled={!combined.firstAvailableDate || combined.is_same_day}
                                            interval={30}
                                            label="Pickup Time"
                                            filter={({ local }) => {
                                                const hours = local.getHours();
                                                const minutes = local.getMinutes();
                                                return hours >= 6 && (hours < 20 || (hours === 20 && minutes === 0));
                                            }}
                                            InputLabelProps={{
                                                shrink: !!combined.pickup_window_end,
                                            }}
                                            style={{ width: '100%' }}
                                            css={css`
                                                display: flex;
                                                justify-content: flex-start;
                                                color: #212529;

                                                .MuiFormControl-root {
                                                    width: 100%;
                                                }

                                                .MuiSelect-root {
                                                    padding: 18.5px 14px;
                                                    padding-right: 32px;
                                                    font-size: 16px;
                                                }

                                                .MuiInputBase-root fieldset {
                                                    top: -5px;
                                                }
                                            `}
                                        />
                                    </Grid>
                                </Grid>

                                {requireShipper || shippers.length > 0 ? (
                                    <>
                                        <Grid
                                            css={css`
                                                margin-bottom: 24px;
                                            `}
                                        >
                                            <SectionTitle>Additional Details</SectionTitle>
                                        </Grid>

                                        <Grid item className="mt-3" container alignItems="center" xs={6}>
                                            <Grid item xs={11}>
                                                <TextField
                                                    select
                                                    variant="outlined"
                                                    fullWidth
                                                    label={`Shipper${requireShipper ? ' *' : ''}`}
                                                    value={state.pickupInfo.shipper_id || ''}
                                                    onChange={(e) => {
                                                        const shipper_id = e.target.value;
                                                        updatePickup({
                                                            shipper_id,
                                                            carrier_id: user_id !== shipper_id ? user_id : null,
                                                        });
                                                    }}
                                                    onBlur={() => setDirty(PICKUP_INFO_FIELDS.SHIPPER_ID)}
                                                    error={
                                                        hasError[PICKUP_INFO_FIELDS.SHIPPER_ID] &&
                                                        isDirty[PICKUP_INFO_FIELDS.SHIPPER_ID]
                                                    }
                                                    helperText={
                                                        shippers.length === 0
                                                            ? 'No shipping partners available'
                                                            : undefined
                                                    }
                                                >
                                                    {shippers.map((shipper) => (
                                                        <MenuItem key={shipper.shipper_id} value={shipper.shipper_id}>
                                                            {shipper.shipper.business_name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={1} container justifyContent="center">
                                                <Tooltip
                                                    style={{ color: '#59B863' }}
                                                    title={
                                                        'Here you can choose a partnered shipper for all orders in this current import. Changing this value will overwrite any already existing partner shipper selections you have made on individual orders.'
                                                    }
                                                    placement="top"
                                                >
                                                    <Info />
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : null}
                            </>
                        )}
                    </>
                ) : null}
            </ModalContent>
            <StickyModalActions border="true">
                <SecondaryButton
                    onClick={() => {
                        reset();
                        callbacks.revert();
                    }}
                >
                    {state.isFirstStep ? 'Cancel' : 'Back'}
                </SecondaryButton>
                <PrimaryButton
                    onClick={() => {
                        const updates = {};

                        if (!combined.firstAvailableDate) {
                            let today = new Date(new Date().setHours(0, 0, 0, 0));
                            updates.firstAvailableDate = today.toISOString();
                        }

                        const hasAddressLine2 = !!combined[PICKUP_INFO_FIELDS.ADDRESS_LINE_2];
                        updates[PICKUP_INFO_FIELDS.RESIDENCE_TYPE] = hasAddressLine2 ? 'Apartment' : '';

                        reset();
                        callbacks.updatePickupInfo({ ...combined, ...updates });
                        callbacks.advance();
                    }}
                    loading={geocodeInflight}
                    disabled={!isValid || geocodeInflight}
                >
                    Next
                </PrimaryButton>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default PickupInfoModal;
